import { ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { Button } from "primeng/button";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { TooltipModule } from 'primeng/tooltip';
import { Table, TableModule } from 'primeng/table';
import { UserService } from '../../services/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogService } from '../../services/log.service';
import _ from 'lodash';
import { User } from '../../models/user';
import { TagModule } from 'primeng/tag';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { RoleOption } from '../../models/roleOption';
import { RoleService } from '../../services/role.service';
import { ChipModule } from 'primeng/chip';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ApiError } from '../../models/apiError';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChangePasswordDialogComponent } from '../../dialogs/change-password-dialog/change-password-dialog.component';
import { AuxiliarService } from '../../services/auxiliar.service';
import { SearchFieldComponent } from "../../components/search-field/search-field.component";

@Component({
  selector: 'app-user-admin',
  imports: [
    Button,
    ChipModule,
    CommonModule,
    ConfirmPopupModule,
    FormsModule,
    InputTextModule,
    MultiSelectModule,
    SearchFieldComponent,
    TagModule,
    TableModule,
    TooltipModule,
    TranslatePipe,
  ],
  providers: [
    ConfirmationService,
    DialogService,
  ],
  templateUrl: './user-admin.component.html',
  styleUrl: './user-admin.component.scss',
})
export class UserAdminComponent implements OnInit {
  @ViewChild('usersTable', { static: true }) usersTable!: Table;

  public _ = _;
  public changePasswordRef: DynamicDialogRef | undefined | null;
  private readonly destroy$ = inject(DestroyRef);
  public editingRowKeys: { [key: string]: boolean } = {};
  public isEditing: boolean = false;
  public loadingUsers: boolean = false;
  private newUserCount: number = 0;
  public roles: RoleOption[] = [];
  public users = computed(() => {
    const filter = this.auxiliarService.searchField();
    return _.filter(this.usersRaw(), (u: User) => {
      return _.some(['username'], (field: string) => {
        return _.includes(_.toLower(_.deburr(_.get(u, field))), filter);
      }) || _.some(u.roles, (r) => 
        _.includes(_.toLower(_.deburr(this.translate.instant(`roles.${r}`))), 
        filter
      ));
    });
  });
  public usersCloned: { [id: string]: User } = {};
  public usersRaw = signal<User[]>([]);

  constructor(
    private auxiliarService: AuxiliarService,
    private cdr: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private logService: LogService,
    private roleService: RoleService,
    private translate: TranslateService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.roles = this.roleService.getRoles();
    this.getUsers();
  }

  private getUsers(): void {
    this.userService.getUsers().pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res) => {
        this.usersRaw.set(_.map(_.get(res, 'data', []), (user: User) => {
          return {
            ...user,
            roles: _.split(_.get(user.roles?.[0], 'name'), ',') as RoleOption[]
          }
        }));
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.logService.error('[Center] - getUsers', err, this.translate.instant('error.users'));
      }
    })
  }

  public addUser(): void {
    this.isEditing = true;
    const user = {id: `new-${++this.newUserCount}`};
    this.usersRaw.set([user, ...this.usersRaw()])
    this.usersTable.first = 0;
    this.usersTable.initRowEdit(user);
    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  public disableUser(event: Event, user: User): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant('confirmation.user-delete'),
      icon: 'pi pi-info-circle',
      acceptLabel: this.translate.instant('yes'),
      rejectLabel: this.translate.instant('no'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.userService.disableUser(user).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
          next: (res) => {
            this.logService.info('[User] - disableUser', res, this.translate.instant('success.user-delete'));
            this.getUsers();
          },
          error: (err: ApiError) => {
            this.logService.error('[User] - disableUser', err, this.translate.instant('error.user-delete'))
          }
        })
      },
      reject: () => {}
    })
  }

  public changePassword(userId: string): void {
    this.changePasswordRef = this.dialogService.open(ChangePasswordDialogComponent, {
      header: this.translate.instant('title.password-change'),
      data: userId,
      closable: true,
    });
  }

  public onRowEditInit(user: User): void {
    this.usersCloned[user.id as string] = { ...user };
    this.isEditing = true;
  }

  public onRowEditCancel(user: User, index: number): void {
    this.users()[index] = this.usersCloned[user.id as string];
    if(_.includes(user.id, 'new-')) {
      const usersRaw = _.filter(this.usersRaw(), (user) => !_.includes(user.id, 'new-'));
      this.usersRaw.set(usersRaw);
    }
    delete this.usersCloned[user.id as string];
    this.isEditing = false;
  }

  public onRowEditSave(user: User): void {
    if(!user.username || !user.roles) return;
    let userData: User = _.cloneDeep({...user, rolesArray: user.roles!});
    if(_.includes(user.id, 'new')) {
      // new user
      this.userService.createUser(userData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[User] - createUser', res, this.translate.instant('success.user-create'));
          this.getUsers();
          this.isEditing = false;
        },
        error: (err) => {
          this.logService.error('[User] - createUser', err, this.translate.instant('error.user-create'));
        }
      })
    } else {
      // update user
      this.userService.updateUser(userData).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
        next: (res) => {
          this.logService.info('[User] - updateUser', res);
          this.getUsers();
          this.isEditing = false;
        },
        error: (err) => {
          this.logService.error('[User] - updateUser', err, this.translate.instant('error.user-update'));
        }
      })
    }
  }
}
